import { z } from 'zod';

/**
 * Regex patterns for detecting PO Box addresses.
 * Matches common variations:
 * - "PO Box", "P.O. Box", "P O Box", "POBox"
 * - "Post Office Box"
 * - Case-insensitive
 * - Works with or without spaces and periods
 */
const PO_BOX_PATTERNS = [
    /\bP\.?\s*O\.?\s*Box\b/i, // P.O. Box, PO Box, P O Box, etc.
    /\bPOBox\b/i, // POBox
    /\bPost\s*Office\s*Box\b/i, // Post Office Box
    /\bP\.?\s*O\.?\s*B\b/i, // P.O.B, POB
];

/**
 * Check if an address string contains a PO Box reference.
 *
 * @param address - The address string to validate
 * @returns true if the address is a PO Box, false otherwise
 *
 * Examples:
 * - "PO Box 123" → true
 * - "P.O. Box 456" → true
 * - "123 Main Street" → false
 * - "POBox789" → true
 * - "Post Office Box 100" → true
 */
export const isPOBox = (address: string): boolean => {
    return PO_BOX_PATTERNS.some((pattern) => pattern.test(address));
};

/**
 * Zod schema for validating street addresses (line 1).
 * - Required field
 * - Cannot be a PO Box
 *
 * Use this for KYC and banking-related address inputs where residential addresses are required.
 */
export const zodResidentialAddressValidation = z
    .string()
    .min(1, 'Street address is required')
    .refine((address) => !isPOBox(address), {
        message:
            'PO Boxes are not allowed. Please provide your residential street address',
    });

/**
 * Zod schema for validating optional address line 2 (apartment, suite, etc.).
 * - Optional field
 * - Cannot be a PO Box if provided
 */
export const zodResidentialAddressLine2Validation = z
    .string()
    .optional()
    .refine(
        (address) => {
            if (address == null || address.trim() === '') return true;
            return !isPOBox(address);
        },
        {
            message: 'PO Boxes are not allowed',
        },
    );

/**
 * Validate an address string to ensure it's not a PO Box.
 * Returns true if the address is valid (not a PO Box), false otherwise.
 *
 * @param address - The address to validate
 * @returns true if valid (not a PO Box), false otherwise
 */
export const isValidResidentialAddress = (address: string): boolean => {
    return !isPOBox(address);
};
